(function (window, document) {
  window.componentEvents.on('consumer-rx-form-instantiate', init);

  /**
   * Instantiate automatic validation of the form.
   */
  function init(form) {
    const steps = form.querySelectorAll('[data-rx-step]');
    const nextButtons = form.querySelectorAll('[data-rx-step-nav="next"]');
    const stepButtons = form.querySelectorAll('[data-rx-step-nav]:not([data-rx-step-nav="next"])');
    const submitButtons = form.querySelectorAll('[type="submit"]');

    // First run each step through validation and add an event listener to it.
    steps.forEach((step) => {
      validateStep(step);
      step.addEventListener('change', (event) => {
        validateAndUpdate(event.currentTarget);
      });
    });
    // Then update the state of the advancement and step buttons according to
    // the first step’s validation status.
    updateNextButtons(steps[0]);
    updateStepButtons(steps[0]);
    // Finally, determine the validity of the entire form.
    validateForm();

    window.componentEvents.on(`rx-form-nav-${form.id}`, (data) => {
      const step = form
        .querySelector(`[data-rx-step="${data.activeStepNumber}"]`);
      validateAndUpdate(step);
    });
    window.componentEvents.on(`rx-form-conditional-fields-updated-${form.id}`, (data) => {
      const step = form
        .querySelector(`[data-rx-step="${data.updatedStepNumber}"]`);
      validateAndUpdate(step);
    });
    window.componentEvents.on(`rx-form-file-upload-cleared-${form.id}`, (data) => {
      const step = form
        .querySelector(`[data-rx-step="${data.uploadStepNumber}"]`);
      validateAndUpdate(step);
    });
    // Validate when the form closes: since the form resets when the user
    // closes the form, this prevents the form from being navigable via the
    // progress bar after a reset
    window.componentEvents.on(`rx-form-reset-${form.id}`, () => {
      const step = steps[0];
      validateAndUpdate(step);
    });

    /**
     * Run all validation operations required every time an event occurs, like
     * navigating from step to step or changing a form control’s value.
     *
     * @param {HTMLElement} step - A DOM Node for a step within the form. This
     * step is presumed to be the step that the user is currently viewing.
     */
    function validateAndUpdate(step) {
      validateStep(step);
      updateNextButtons(step);
      updateStepButtons(step);
      validateForm();
    }

    /**
     * Determine whether a step is valid and update its data-rx-valid attribute.
     *
     * @param {HTMLElement} step - A DOM Node for a step within the form. This
     * step is presumed to be the step that the user is currently viewing, with
     * the exception of validation that occurs on initial load, which is run on
     * all steps.
     */
    function validateStep(step) {
      // We evaluate radio, file, and select inputs separately from all others.
      const radios = Array.from(step.querySelectorAll('input[type="radio"][required]'));
      const uploads = Array.from(step.querySelectorAll('input[type="file"][required]'));
      const selects = Array.from(step.querySelectorAll('select[required]'));
      const inputs = Array.from(step.querySelectorAll('input[required]:not([type="radio"]):not([type="file"])'));
      // Validity is determined by testing that all of the following are true:
      // - At least *one* required radio button is checked.
      // - *All* required file inputs have files selected and the files are of an
      //   accepted format.
      // - *All* required select drop downs have an option selected with a
      //   `value` attribute.
      // - *All* other required inputs are checked.
      const valid = radios.some((radio) => radio.checked) &&
        uploads.every(validateUploadData) &&
        selects.every((select) => select.querySelector('option:checked').getAttribute('value') !== null) &&
        inputs.every((input) => input.checked);
      // Track validity in a data attribute on the step.
      step.dataset.rxValid = valid;
    }

    /**
     * Check that a file selected for upload meets the upload criteria.
     *
     * @param {Element} upload - An input Node of type="file".
     */
    function validateUploadData(upload) {
      // Immediately return false if no file is selected.
      if (!upload.files.length) return false;

      // Remove any previous upload error messages in the form.
      form.querySelectorAll('label.upload-error-msg')
        .forEach((label) => label.remove());

      const errors = [];
      // For each file selected, check its size and type.
      for (let i = 0; i < upload.files.length; i++) {
        // The Input.files property returns a FileList object, which has an item
        // method that accepts an index and returns the File object at that
        // position.
        const file = upload.files.item(i);
        // If file type is incorrect or an empty string, push error message to
        // array.
        if (file.type &&
            !file.type.match(/^(application|image)\/(heic|heif|jpg|jpeg|pdf|png)$/)) {
          errors.push(`File ${file.name} is not the right format (${file.type}). Please upload a JPG, PNG, PDF, or HEIC/HEIF file.`);
        }
        // If file size is greater than 5MB, push error message to array.
        if (file.size > 5000000) {
          errors.push(`File ${file.name} is too large. Please upload a file no larger than 5MB.`);
        }
      }

      // For every error message in array, create and inject label element.
      errors.forEach((error) => {
        const label = document.createElement('label');
        label.setAttribute('for', upload.id);
        label.classList.add('upload-error-msg');
        label.innerText = error;
        upload.after(label);
      });

      const isValid = errors.length === 0;

      // Send message to show file preview if file is valid.
      if (isValid) {
        window.componentEvents.emitEvent(`rx-form-upload-file-valid-${form.id}`);
      } else {
        window.componentEvents.emitEvent(`rx-form-upload-file-invalid-${form.id}`);
      }

      // Return an assertion that no errors were detected for this file input.
      return isValid;
    }

    /**
     * Update the state of any advancement (e.g. Next) buttons found in the form.
     *
     * @param {HTMLElement} step - A DOM Node for a step within the form. This
     * step is presumed to be the step that the user is currently viewing.
     */
    function updateNextButtons(step) {
      const valid = step.dataset.rxValid === 'true';
      // Update state of advancement buttons.
      nextButtons.forEach((button) => {
        valid ? button.removeAttribute('disabled') :
          button.setAttribute('disabled', true);
      });
    }

    /**
     * Update the state of any step-specific buttons found in the form.
     *
     * @param {HTMLElement} step - A DOM Node for a step within the form. This
     * step is presumed to be the step that the user is currently viewing.
     */
    function updateStepButtons(step) {
      // If form is skipping config, we treat buttons differently.
      if (form.dataset.rxSkipConfigurationEnabled === 'true') {
        stepButtons.forEach((button) => {
          const buttonStep = parseInt(button.dataset.rxStepNav);
          // When skipping config, only the first step is available.
          buttonStep === 0 ? button.removeAttribute('disabled') :
            button.setAttribute('disabled', true);
        });
        return;
      }
      const valid = step.dataset.rxValid === 'true';
      const selectedStep = parseInt(step.dataset.rxStep);
      stepButtons.forEach((button) => {
        const buttonStep = parseInt(button.dataset.rxStepNav);
        if (valid) {
          // If step is validated, enable all buttons belonging to:
          //   - this step
          //   - all steps before this step
          //   - the next step
          buttonStep <= selectedStep + 1 ? button.removeAttribute('disabled') :
            button.setAttribute('disabled', true);
        } else {
          // If step is *not* validated, enable only the buttons belonging to:
          //   - this step
          //   - all steps before this step
          buttonStep <= selectedStep ? button.removeAttribute('disabled') :
            button.setAttribute('disabled', true);
        }
      });
    }

    /**
     * Determine whether all the steps are valid and update the form element’s
     * data-rx-valid attribute.
     */
    function validateForm() {
      // Construct an array of steps to examine.
      const valid = Array.from(steps)
      // Filter out any steps that are skipped.
        .filter((step) => step.dataset.rxSkipStep !== 'true')
      // Assert that every remaining step has been marked as valid.
        .every((step) => step.dataset.rxValid === 'true');

      // Track validity in a data attribute on the form.
      form.dataset.rxValid = valid;
      // Update state of submission buttons.
      submitButtons.forEach((button) => {
        valid ? button.removeAttribute('disabled') :
          button.setAttribute('disabled', true);
      });
    }
  }
})(window, document);
